import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { getLifecycleStage } from "@/v2/lib/bienLifecycle";
import type { BienRow } from "@/schemas/bien";
import { cn } from "@/lib/utils";

const STAGE_COLORS: Record<string, string> = {
  danger: "bg-danger/15 text-danger",
  warning: "bg-warning/15 text-warning",
  accent: "bg-accent/15 text-accent",
  success: "bg-success/15 text-success",
  muted: "bg-gray-100 text-text-muted",
};

interface FicheHeaderProps {
  /** Élément déjà passé par le helper `F()` (null si le rôle ne le voit pas). */
  disponibiliteField: ReactNode;
  /** État SAUVÉ du bien — l'étape se déduit de la base, pas du brouillon. */
  bien: BienRow;
}

/**
 * En-tête de fiche — HORS onglets (spec « fiche en onglets », 2026-08-07).
 *
 * Deux informations lues en permanence, sur UNE SEULE LIGNE : le statut du bien
 * et le badge d'étape du cycle. Libellés à gauche de leur contrôle
 * (`inlineLabel`) pour que la ligne reste compacte et alignée.
 *
 * `transaction` a été RETIRÉE de l'en-tête (demande Thibaud 2026-08-08) : cette
 * fiche est la fiche LOCATION — un bien en vente est rendu par
 * BienVenteDetailPage — et le fil d'Ariane porte déjà le badge « Location ».
 * L'information était donc affichée trois fois. Conséquence assumée : plus aucun
 * chemin de conversion location↔vente depuis la fiche, y compris pour l'admin ;
 * la duplication vers l'autre catalogue reste le chemin prévu (cf. migration 101).
 *
 * Le badge d'étape est dérivé (`getLifecycleStage`), jamais saisi : mêmes
 * libellés et couleurs que le Pipeline kanban et les tuiles du dashboard, pour
 * qu'un même bien dise la même chose partout.
 */
export function FicheHeader({ disponibiliteField, bien }: FicheHeaderProps) {
  const { t } = useTranslation();
  const stage = getLifecycleStage(bien);

  return (
    /* Pas de `mb-*` ici : le conteneur de la fiche applique déjà `gap-6`.
       Les deux se cumulaient et donnaient 48 px sous le trait, soit le double
       de la gouttière entre le menu de gauche et les cards (24 px). */
    <div className="flex flex-wrap items-center gap-x-10 gap-y-3 pb-4 border-b border-border">
      {/* Mis en avant (demande Thibaud 2026-08-19, x2 — le premier essai en
          accent/5 + accent/30 passait inaperçu) : c'est LE champ qui
          conditionne tout le reste de la fiche (cycle, mailing, sous option…),
          mais rendu nu il se confondait avec un champ secondaire quelconque.
          Contour PLEIN + fond nettement teinté, pas une nuance discrète. */}
      <div className="rounded-md border-2 border-accent bg-accent/10 px-3 py-1.5 shadow-sm">
        {disponibiliteField}
      </div>
      <div className="flex flex-row items-center gap-3">
        <span className="text-xs font-medium tracking-wider uppercase text-text-muted whitespace-nowrap">
          {t("biens.etapeDuCycle")}
        </span>
        <span
          className={cn(
            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap",
            STAGE_COLORS[stage.color],
          )}
          title={
            stage.missingFields.length > 0
              ? t("biens.etapeBloqueeHint", { count: stage.missingFields.length })
              : undefined
          }
        >
          {stage.stage === "draft_clevel" && <AlertTriangle size={11} />}
          {t(stage.labelKey)}
        </span>
      </div>
    </div>
  );
}
